import { Popover } from 'antd'
import styled from 'styled-components'
import { useState } from 'react'
import WizrButton from '../WizrButton'
import WizrFlexLayout from '../WizrFlexLayout'
import MoreIcon from '../../assets/icons/More'
import EditIcon from '../../assets/icons/EditIcon'
import DeleteIcon from '../../assets/icons/DeleteIcon'
import { MODAL_TYPES, useWizrModalContext } from '../../providers/modal/WizrModalProvider'
import { dispatchDeleteLlmConfig, dispatchUpdateLlmConfig } from '../../redux/actions/content'
import { buildLlmConfigApiPayload, isCreateLlmDbAiPayload } from '../forms/CreateLlmDbForm'

const StyledButton = styled(WizrButton)`
  border-radius: 50%;
  width: 20px;
  height: 20px;
  padding-right: 8px;
  justify-content: center;
  align-items: center;
  path {
    stroke: #999b9f;
    transition: stroke 0.3s ease;
  }
  &:hover path {
    stroke: #1d1f22;
  }
`

const LlmDbRowActionPopoverContent = ({
  onEdit,
  onDelete,
}: {
  onEdit: () => void
  onDelete: () => void
}): JSX.Element => {
  return (
    <WizrFlexLayout textAlign='left' background='#FFF'>
      <WizrButton
        icon={<EditIcon />}
        text='Edit'
        type='text'
        justifyContent='flex-start'
        onClick={onEdit}
      />
      <WizrButton
        icon={<DeleteIcon />}
        text='Delete'
        type='text'
        justifyContent='flex-start'
        onClick={onDelete}
      />
    </WizrFlexLayout>
  )
}

export type LlmDbTableRow = {
  _source?: Record<string, unknown>
  id?: string
}

const LlmDbRowActionPopover = ({ record }: { record?: LlmDbTableRow }): JSX.Element => {
  const [open, setOpen] = useState(false)
  const { showModal, hideModal } = useWizrModalContext()
  const source = (record?._source ?? record) as Record<string, unknown> | undefined

  const handleEdit = () => {
    setOpen(false)
    if (!source || typeof source !== 'object') return
    showModal(MODAL_TYPES.CREATE_LLM_DB_AI, {
      variant: 'ai',
      initialRecord: source,
      modalTitle: 'Edit AI',
      onCancel: hideModal,
      onSave: async (payload: unknown) => {
        if (!isCreateLlmDbAiPayload(payload)) return
        const ok = await dispatchUpdateLlmConfig(buildLlmConfigApiPayload(payload))
        if (ok) hideModal()
      },
    })
  }

  const handleDelete = () => {
    setOpen(false)
    const id =
      source?.id != null
        ? String(source.id)
        : record?.id != null
          ? String(record.id)
          : ''
    if (!id) return
    const name =
      (source?.display_name != null ? String(source.display_name) : '') ||
      (source?.model_name != null ? String(source.model_name) : '') ||
      'this LLM configuration'
    showModal(MODAL_TYPES.CONFIRMATION, {
      text: `Are you sure you want to delete ${name} ?`,
      buttontext: 'Confirm',
      onConfirm: async () => {
        const ok = await dispatchDeleteLlmConfig(id)
        if (ok) hideModal()
      },
      onCancel: hideModal,
    })
  }

  return (
    <Popover
      open={open}
      onOpenChange={setOpen}
      content={<LlmDbRowActionPopoverContent onEdit={handleEdit} onDelete={handleDelete} />}
      trigger='click'
    >
      <StyledButton
        icon={<MoreIcon />}
        text=''
        type='text'
        aria-label='Row actions'
        style={{ height: '40px', width: '40px', borderRadius: '50px' }}
      />
    </Popover>
  )
}

export default LlmDbRowActionPopover
